'use client'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { useI18n } from '@/lib/i18n'
import { gameQueries } from '@/lib/queries'
import { useTRPC } from '@/lib/trpc'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Minus, RefreshCw } from 'lucide-react'
import { useState } from 'react'

export function ControlPanel() {
  const { t } = useI18n()
  const trpc = useTRPC()
  const jevQuery = useQuery({
    ...trpc.queryRouter.getJevRouting.queryOptions(),
    retry: false,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  })
  const jevMutation = useMutation({
    ...trpc.queryRouter.setJevRouting.mutationOptions(),
    onSettled: async () => {
      await jevQuery.refetch()
    },
  })
  const jevBusy = jevQuery.isFetching || jevMutation.isPending
  const steamPrereleaseQuery = useQuery({
    ...trpc.queryRouter.getSteamPrereleaseAccess.queryOptions(),
    retry: false,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  })
  const steamPrerelease = steamPrereleaseQuery.isError
    ? undefined
    : steamPrereleaseQuery.data
  const jevStatusKnown = !!jevQuery.data && !jevQuery.isError
  const [isRefreshingModels, setIsRefreshingModels] = useState(false)
  const [modelRefreshMessage, setModelRefreshMessage] = useState<{
    type: 'success' | 'error'
    text: string
  } | null>(null)
  const [isLoadingModels, setIsLoadingModels] = useState(false)
  const [responsiveLowCost, setResponsiveLowCost] = useState<string[]>([])
  const [responsiveHighPerf, setResponsiveHighPerf] = useState<string[]>([])
  const [poolModels, setPoolModels] = useState<string[]>([])

  const handleRefreshResponsiveModels = async () => {
    setIsRefreshingModels(true)
    setModelRefreshMessage(null)

    try {
      const result = await gameQueries.refreshResponsiveModelsCache()
      setModelRefreshMessage({
        type: result.success ? 'success' : 'error',
        text: result.message ?? 'Responsive model cache refreshed successfully.',
      })
    }
    catch (error) {
      console.error('Failed to refresh responsive models cache:', error)
      setModelRefreshMessage({
        type: 'error',
        text: error instanceof Error ? error.message : String(error),
      })
    }
    finally {
      setIsRefreshingModels(false)
    }
  }

  const handleShowResponsiveModels = async () => {
    setIsLoadingModels(true)

    try {
      const [responsiveResult, poolsResult] = await Promise.all([
        gameQueries.getResponsiveModels(),
        gameQueries.getGameModelPools(),
      ])
      setResponsiveLowCost(responsiveResult.data.lowCost || [])
      setResponsiveHighPerf(responsiveResult.data.highPerformance || [])

      if (poolsResult.success && poolsResult.data) {
        const low = Object.values(poolsResult.data.lowCost ?? {}).flat()
        const high = Object.values(poolsResult.data.highPerformance ?? {}).flat()
        setPoolModels([...new Set([...low, ...high])])
      }
    }
    catch (error) {
      console.error('Failed to get responsive models or pools:', error)
    }
    finally {
      setIsLoadingModels(false)
    }
  }

  const allModels = poolModels.length > 0 ? poolModels : []
  const allResponsive = [...responsiveLowCost, ...responsiveHighPerf]

  const slowModels = allResponsive.length > 0 && allModels.length > 0
    ? allModels.filter(model => !allResponsive.includes(model))
    : []

  return (
    <div className="bg-slate-50 px-4 py-8 dark:bg-slate-900 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="mb-8 text-center">
          <h2 className="mb-2 text-xl font-bold text-foreground md:text-2xl lg:text-3xl">
            {t('admin.controlPanel.title', 'Control Panel')}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t('admin.controlPanel.description', 'System administration and cache management tools')}
          </p>
        </div>

        <div className="grid gap-6">
          <Card>
            <CardHeader>
              <CardTitle>
                {t('admin.controlPanel.gameModels', 'Game Models')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-4">
                <div className="space-y-2" aria-busy={jevBusy}>
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex min-h-9 items-center gap-2">
                      {jevStatusKnown ? (
                        <Checkbox
                          id="global-jev-enabled"
                          checked={jevQuery.data!.enabled}
                          disabled={jevBusy}
                          onCheckedChange={checked => jevMutation.mutate({ enabled: checked === true })}
                          aria-describedby="global-jev-status"
                        />
                      ) : (
                        <span
                          role="img"
                          aria-label={jevQuery.isError
                            ? t('admin.controlPanel.jevLoadError', 'Could not read Jev status. Reload to try again.')
                            : t('admin.controlPanel.jevLoading', 'Reading Jev status...')}
                          className="flex size-4 shrink-0 items-center justify-center rounded-sm border border-input text-muted-foreground"
                        >
                          <Minus className="size-3" aria-hidden="true" />
                        </span>
                      )}
                      <Label htmlFor={jevStatusKnown ? 'global-jev-enabled' : undefined}>
                        {t('admin.controlPanel.jevEnabled', 'Jev enabled')}
                      </Label>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={jevBusy}
                      onClick={() => {
                        jevMutation.reset()
                        void jevQuery.refetch()
                      }}
                    >
                      <RefreshCw className={`mr-2 size-4 ${jevBusy ? 'animate-spin' : ''}`} aria-hidden="true" />
                      {t('admin.controlPanel.jevReload', 'Reload Jev status')}
                    </Button>
                  </div>
                  <p id="global-jev-status" role="status" className="text-sm text-muted-foreground">
                    {jevMutation.isPending
                      ? t('admin.controlPanel.jevSaving', 'Saving Jev setting...')
                      : jevQuery.isFetching
                        ? t('admin.controlPanel.jevLoading', 'Reading Jev status...')
                        : t('admin.controlPanel.jevScope', 'Applies globally to new eligible actions.')}
                  </p>
                  {(jevQuery.isError || jevMutation.isError) && (
                    <p role="alert" className="text-sm text-destructive">
                      {jevQuery.isError
                        ? t('admin.controlPanel.jevLoadError', 'Could not read Jev status. Reload to try again.')
                        : t('admin.controlPanel.jevSaveError', 'Could not confirm the Jev update. Reload its status and try again.')}
                    </p>
                  )}
                </div>
                <div className="flex gap-4">
                  <Button
                    onClick={handleRefreshResponsiveModels}
                    disabled={isRefreshingModels}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    {isRefreshingModels ? (
                      <>
                        <RefreshCw className="size-4 animate-spin" />
                        Calculating...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="size-4" />
                        Calculate Responsive Model
                      </>
                    )}
                  </Button>

                  <Button
                    onClick={handleShowResponsiveModels}
                    disabled={isLoadingModels}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    {isLoadingModels ? (
                      <>
                        <RefreshCw className="size-4 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="size-4" />
                        Show Responsive Model
                      </>
                    )}
                  </Button>
                </div>

                {modelRefreshMessage && (
                  <p
                    role={modelRefreshMessage.type === 'error' ? 'alert' : 'status'}
                    className={`rounded-md border px-3 py-2 text-sm ${
                      modelRefreshMessage.type === 'success'
                        ? 'border-green-200 bg-green-50 text-green-700 dark:border-green-900 dark:bg-green-950/30 dark:text-green-300'
                        : 'border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300'
                    }`}
                  >
                    {modelRefreshMessage.text}
                  </p>
                )}

                {(responsiveLowCost.length > 0 || responsiveHighPerf.length > 0) && (
                  <>
                    <div className="mt-4 grid gap-6 md:grid-cols-2">
                      { }
                      <div>
                        <h4 className="mb-2 text-sm font-medium text-green-600 dark:text-green-400">
                          Low Cost Responsive ({responsiveLowCost.length})
                        </h4>
                        <div className="rounded-md bg-slate-50 p-3 dark:bg-slate-800">
                          {responsiveLowCost.length > 0
                            ? (
                                <div className="grid grid-cols-1 gap-2">
                                  {responsiveLowCost.map(model => (
                                    <div key={model} className="truncate rounded border border-green-200 bg-white px-2 py-1 font-mono text-xs dark:border-green-900 dark:bg-slate-700">
                                      {model}
                                    </div>
                                  ))}
                                </div>
                              )
                            : (
                                <div className="text-xs text-muted-foreground">No models available</div>
                              )}
                        </div>
                      </div>

                      { }
                      <div>
                        <h4 className="mb-2 text-sm font-medium text-blue-600 dark:text-blue-400">
                          High Performance Responsive ({responsiveHighPerf.length})
                        </h4>
                        <div className="rounded-md bg-slate-50 p-3 dark:bg-slate-800">
                          {responsiveHighPerf.length > 0
                            ? (
                                <div className="grid grid-cols-1 gap-2">
                                  {responsiveHighPerf.map(model => (
                                    <div key={model} className="truncate rounded border border-blue-200 bg-white px-2 py-1 font-mono text-xs dark:border-blue-900 dark:bg-slate-700">
                                      {model}
                                    </div>
                                  ))}
                                </div>
                              )
                            : (
                                <div className="text-xs text-muted-foreground">No models available</div>
                              )}
                        </div>
                      </div>
                    </div>

                    {slowModels.length > 0 && (
                      <div className="mt-4">
                        <h4 className="mb-2 text-sm font-medium text-red-600 dark:text-red-400">
                          Slow/Unresponsive Models ({slowModels.length}):
                        </h4>
                        <div className="rounded-md bg-slate-50 p-3 dark:bg-slate-800">
                          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                            {slowModels.map(model => (
                              <div key={model} className="truncate rounded border border-red-200 bg-white px-2 py-1 font-mono text-sm dark:border-red-900 dark:bg-slate-700">
                                {model}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>
                {t('admin.controlPanel.steamPrerelease', 'Steam Prerelease access')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2" aria-busy={steamPrereleaseQuery.isFetching}>
                <p
                  role="status"
                  data-testid="steam-prerelease-status"
                  className="text-sm font-medium text-foreground"
                >
                  {steamPrerelease
                    ? steamPrerelease.enabled
                      ? t('admin.controlPanel.steamPrereleaseOn', 'On')
                      : t('admin.controlPanel.steamPrereleaseOff', 'Off')
                    : steamPrereleaseQuery.isError
                      ? t('admin.controlPanel.steamPrereleaseLoadError', 'Could not read Steam Prerelease access.')
                      : t('admin.controlPanel.steamPrereleaseLoading', 'Reading Steam Prerelease access...')}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t(
                    'admin.controlPanel.steamPrereleaseScope',
                    'While on, every Steam account has unlimited Free Games and receives a one-time {amount} test credit grant at its next Steam login. Website accounts are not affected.',
                  ).replace(
                    '{amount}',
                    steamPrerelease
                      ? `$${(steamPrerelease.testCredits / 1_000_000).toFixed(2)}`
                      : '—',
                  )}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t(
                    'admin.controlPanel.steamPrereleaseSource',
                    'Set STEAM_PRERELEASE_ACCESS=true on the API servers only while the Steam app is in Prerelease. It is off by default.',
                  )}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
